import React, { useState, useMemo, useCallback, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import { useSnackbar } from "notistack";
import {
  usePreviewMergeChallenges,
  useMergeChallenges,
} from "../../../api/hooks/useChallenges";
import {
  ConflictResolution,
  type MergeConflictDto,
  type MergePreviewResponseDto,
} from "../../../api/types/dtos/challenge/ChallengeDto";

interface TemplateItem {
  challengeId: string;
  name: string;
  orgCount: number;
}

interface MergeChallengesModalProps {
  open: boolean;
  onClose: () => void;
  templates: TemplateItem[];
}

const STEPS = ["Seleccionar retos", "Conflictos", "Resultado"];

const MergeChallengesModal: React.FC<MergeChallengesModalProps> = ({
  open,
  onClose,
  templates,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const previewMutation = usePreviewMergeChallenges();
  const mergeMutation = useMergeChallenges();

  const [activeStep, setActiveStep] = useState(0);
  const [targetChallengeId, setTargetChallengeId] = useState<string>("");
  const [selectedSources, setSelectedSources] = useState<string[]>([]);
  const [manualResolutions, setManualResolutions] = useState<
    Record<string, string>
  >({});
  const [previewData, setPreviewData] = useState<MergePreviewResponseDto | null>(
    null
  );
  const [mergeResult, setMergeResult] = useState<{
    renamed: number;
    softDeleted: number;
    skipped: number;
    errors: string[];
  } | null>(null);

  const reset = useCallback(() => {
    setActiveStep(0);
    setTargetChallengeId("");
    setSelectedSources([]);
    setManualResolutions({});
    setPreviewData(null);
    setMergeResult(null);
  }, []);

  useEffect(() => {
    if (open) reset();
  }, [open, reset]);

  const targetTemplate = useMemo(
    () => templates.find((t) => t.challengeId === targetChallengeId),
    [templates, targetChallengeId]
  );
  void targetTemplate;

  const sourceTemplates = useMemo(
    () => templates.filter((t) => t.challengeId !== targetChallengeId),
    [templates, targetChallengeId]
  );

  const handleSourceToggle = (id: string) => {
    setSelectedSources((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  };

  const handlePreview = useCallback(async () => {
    if (!targetChallengeId || selectedSources.length === 0) return;
    try {
      const result = await previewMutation.mutateAsync({
        targetChallengeId,
        sourceChallengeIds: selectedSources,
      });
      setPreviewData(result.data);
      const hasManual =
        result.data.conflicts?.some(
          (c) => c.resolution === ConflictResolution.MANUAL
        ) ?? false;
      setActiveStep(hasManual ? 1 : 2);
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { description?: string } } })?.response
          ?.data?.description ||
        (e instanceof Error ? e.message : "Error al previsualizar");
      enqueueSnackbar(msg, { variant: "error" });
    }
  }, [targetChallengeId, selectedSources, previewMutation, enqueueSnackbar]);

  const allManualResolved = useMemo(() => {
    if (!previewData) return true;
    const manualConflicts = previewData.conflicts.filter(
      (c) => c.resolution === ConflictResolution.MANUAL
    );
    return manualConflicts.every((c) => manualResolutions[c.inspectedOrgId]);
  }, [previewData, manualResolutions]);

  const handleConfirmMerge = useCallback(async () => {
    if (!targetChallengeId || selectedSources.length === 0 || !previewData)
      return;

    const manualConflicts = previewData.conflicts.filter(
      (c) => c.resolution === ConflictResolution.MANUAL
    );
    const resolutions = manualConflicts
      .filter((c) => manualResolutions[c.inspectedOrgId])
      .map((c) => ({
        inspectedOrgId: c.inspectedOrgId,
        keepInspectionId: manualResolutions[c.inspectedOrgId],
      }));

    try {
      const result = await mergeMutation.mutateAsync({
        targetChallengeId,
        sourceChallengeIds: selectedSources,
        resolutions,
      });
      setMergeResult(result.data);
      setActiveStep(2);
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { description?: string } } })?.response
          ?.data?.description ||
        (e instanceof Error ? e.message : "Error al combinar");
      enqueueSnackbar(msg, { variant: "error" });
    }
  }, [
    targetChallengeId,
    selectedSources,
    previewData,
    manualResolutions,
    mergeMutation,
    enqueueSnackbar,
  ]);

  const canProceedFromStep0 =
    targetChallengeId && selectedSources.length > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Combinar Retos</DialogTitle>
      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mb: 3, mt: 1 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Step 0: Select target + sources */}
        {activeStep === 0 && (
          <Box>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
              1. Selecciona el reto destino
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Las organizaciones de los retos origen se moveran a este reto.
            </Typography>
            <RadioGroup
              value={targetChallengeId}
              onChange={(e) => {
                setTargetChallengeId(e.target.value);
                setSelectedSources([]);
              }}
            >
              {templates.map((t) => (
                <FormControlLabel
                  key={t.challengeId}
                  value={t.challengeId}
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography component="span" fontWeight={500}>
                        {t.name}
                      </Typography>
                      <Typography
                        component="span"
                        variant="body2"
                        color="text.secondary"
                        sx={{ ml: 1 }}
                      >
                        ({t.orgCount} org)
                      </Typography>
                    </Box>
                  }
                />
              ))}
            </RadioGroup>

            {targetChallengeId && (
              <>
                <Typography
                  variant="subtitle1"
                  fontWeight={700}
                  sx={{ mt: 3, mb: 1 }}
                >
                  2. Selecciona los retos origen
                </Typography>
                {sourceTemplates.length === 0 ? (
                  <Alert severity="info">
                    No hay otros retos disponibles para combinar.
                  </Alert>
                ) : (
                  sourceTemplates.map((t) => (
                    <FormControlLabel
                      key={t.challengeId}
                      control={
                        <Checkbox
                          checked={selectedSources.includes(t.challengeId)}
                          onChange={() => handleSourceToggle(t.challengeId)}
                        />
                      }
                      label={
                        <Box>
                          <Typography component="span" fontWeight={500}>
                            {t.name}
                          </Typography>
                          <Typography
                            component="span"
                            variant="body2"
                            color="text.secondary"
                            sx={{ ml: 1 }}
                          >
                            ({t.orgCount} org)
                          </Typography>
                        </Box>
                      }
                    />
                  ))
                )}
              </>
            )}
          </Box>
        )}

        {/* Step 1: Conflict resolution */}
        {activeStep === 1 && previewData && (
          <Box>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
              Conflictos detectados
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Las siguientes fincas existen en ambos retos. Elige cual inspeccion
              conservar para cada conflicto.
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Finca</TableCell>
                    <TableCell align="center">
                      Origen (respuestas)
                    </TableCell>
                    <TableCell align="center">
                      Destino (respuestas)
                    </TableCell>
                    <TableCell align="center">Decision</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {previewData.conflicts.map((conflict: MergeConflictDto) => (
                    <ConflictRow
                      key={conflict.inspectedOrgId}
                      conflict={conflict}
                      resolution={manualResolutions[conflict.inspectedOrgId]}
                      onChange={(val) =>
                        setManualResolutions((prev) => ({
                          ...prev,
                          [conflict.inspectedOrgId]: val,
                        }))
                      }
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Alert severity="info" sx={{ mt: 2 }}>
              {previewData.farmsToRename} finca(s) se moveran sin conflicto.
            </Alert>
          </Box>
        )}

        {/* Step 2: Result */}
        {activeStep === 2 && mergeResult && (
          <Box>
            <Alert severity="success" sx={{ mb: 2 }}>
              Combinacion completada exitosamente.
            </Alert>
            <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
              <Typography variant="body1">
                <strong>{mergeResult.renamed}</strong> finca(s) movida(s)
              </Typography>
              <Typography variant="body1">
                <strong>{mergeResult.softDeleted}</strong> inspeccion(es)
                eliminada(s)
              </Typography>
              <Typography variant="body1">
                <strong>{mergeResult.skipped}</strong> omitida(s)
              </Typography>
            </Box>
            {mergeResult.errors.length > 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {mergeResult.errors.length} error(es):{" "}
                {mergeResult.errors.join(", ")}
              </Alert>
            )}
          </Box>
        )}

        {/* Summary view when skipping to step 2 (no manual conflicts) */}
        {activeStep === 2 && !mergeResult && previewData && (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              No hay conflictos manuales. Todo se resolvera automaticamente.
            </Alert>
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>{previewData.farmsToRename}</strong> finca(s) se moveran al
              reto &ldquo;{previewData.targetChallengeName}&rdquo;.
            </Typography>
            {previewData.conflicts.length > 0 && (
              <Typography variant="body2" color="text.secondary">
                {previewData.conflicts.length} conflicto(s) auto-resuelto(s).
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          {activeStep === 2 ? "Cerrar" : "Cancelar"}
        </Button>
        {activeStep === 0 && (
          <Button
            variant="contained"
            onClick={handlePreview}
            disabled={!canProceedFromStep0 || previewMutation.isPending}
            startIcon={
              previewMutation.isPending ? (
                <CircularProgress size={16} />
              ) : undefined
            }
          >
            {previewMutation.isPending ? "Analizando..." : "Previsualizar"}
          </Button>
        )}
        {activeStep === 1 && (
          <Button
            variant="contained"
            onClick={handleConfirmMerge}
            disabled={!allManualResolved || mergeMutation.isPending}
            startIcon={
              mergeMutation.isPending ? (
                <CircularProgress size={16} />
              ) : undefined
            }
          >
            {mergeMutation.isPending ? "Combinando..." : "Combinar"}
          </Button>
        )}
        {activeStep === 2 && !mergeResult && previewData && (
          <Button
            variant="contained"
            onClick={handleConfirmMerge}
            disabled={mergeMutation.isPending}
            startIcon={
              mergeMutation.isPending ? (
                <CircularProgress size={16} />
              ) : undefined
            }
          >
            {mergeMutation.isPending ? "Combinando..." : "Confirmar Combinacion"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

// Conflict row with resolution toggle
const ConflictRow: React.FC<{
  conflict: MergeConflictDto;
  resolution: string | undefined;
  onChange: (value: string) => void;
}> = ({ conflict, resolution, onChange }) => {
  const isManual = conflict.resolution === ConflictResolution.MANUAL;
  const autoLabel =
    conflict.resolution === ConflictResolution.AUTO_KEEP_SOURCE
      ? "Conservar origen"
      : conflict.resolution === ConflictResolution.AUTO_KEEP_TARGET
        ? "Conservar destino"
        : "";

  return (
    <TableRow>
      <TableCell>{conflict.inspectedOrgName}</TableCell>
      <TableCell align="center">{conflict.sourceAnsweredItems}</TableCell>
      <TableCell align="center">{conflict.targetAnsweredItems}</TableCell>
      <TableCell align="center">
        {isManual ? (
          <RadioGroup
            row
            value={resolution || ""}
            onChange={(e) => onChange(e.target.value)}
            sx={{ justifyContent: "center" }}
          >
            <FormControlLabel
              value={conflict.sourceInspectionId}
              control={<Radio size="small" />}
              label="Origen"
            />
            <FormControlLabel
              value={conflict.targetInspectionId}
              control={<Radio size="small" />}
              label="Destino"
            />
          </RadioGroup>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {autoLabel}
          </Typography>
        )}
      </TableCell>
    </TableRow>
  );
};

export default MergeChallengesModal;
